import { useState } from "react";

import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { useAppState } from "../context/StateContext";
import { Error } from "../models/error";
import { Filters, Searches } from "../models/shipeezi.table";
import { Supplier, SupplierIds, SupplierInviteParams } from "../models/supplier";
import SupplierService from "../services/supplier.service";
import { checkError } from "../util/checkError";
import { SupplierKeys } from "../util/queries";
import { useSupplierContext } from "./useSupplierContext";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour

const useActionSupplier = () => {
  const { setSupplier } = useSupplierContext();
  const { currentUser, hasUnsavedChanges, setHasUnsavedChanges } = useAppState();
  const queryClient = useQueryClient();
  const [responseError, setResponseError] = useState<Error>();

  const { t: translate } = useTranslation();

  const getAllSuppliers = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: SupplierKeys.getAll([page, pageSize, searches, filters]),
      queryFn: async () => {
        const response = await SupplierService.getAll({
          companyId: currentUser?.companyId,
          include: JSON.stringify([
            { association: "location" },
            { association: "associatedCompany" },
          ]),
          sortBy: JSON.stringify([["updatedAt", "Desc"]]),
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
          ...(!_.isNil(page) && { page }),
          ...(!_.isNil(pageSize) && { pageSize }),
        });

        if (response && response.data && response.data instanceof Array) {
          return response.data;
        }

        return [];
      },

      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const countSuppliers = (
    setTotal: (value: number) => void,
    searchs?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: SupplierKeys.count([searchs, filters]),
      queryFn: () =>
        SupplierService.count({
          companyId: currentUser?.companyId,
          searchByOr: searchs && searchs.length > 0 ? JSON.stringify(searchs) : "",
          ...filters,
        }),
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response && response.data) {
          setTotal(response.data);
        }
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getSupplier = (id: number) => {
    return useQuery({
      queryKey: SupplierKeys.getById(id),
      queryFn: () =>
        SupplierService.getById(id, {
          include: JSON.stringify([
            { association: "location" },
            { association: "warehouses", include: [{ association: "location" }] },
          ]),
        }),
      enabled: !!id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setSupplier(response.data);
        }
      },
    });
  };

  const useSaveSupplier = (callback?: (id?: number) => void) => {
    return useMutation({
      mutationKey: SupplierKeys.save(),
      mutationFn: (supplier: Supplier) => {
        if (supplier.id)
          return SupplierService.update(supplier.id, {
            ...supplier,
            companyId: currentUser?.companyId,
          });

        return SupplierService.create({
          ...supplier,
          companyId: currentUser?.companyId,
        });
      },
      onSuccess: (response) => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback && response) callback(response?.data?.id);

        queryClient.invalidateQueries({ queryKey: [SupplierKeys.all] });
      },
      onError: (error: AxiosError<Error>) => {
        if (axios.isAxiosError(error) && error.response?.data && error.response.data.email) {
          NotificationToast.error(error.response.data.email);
        } else {
          setResponseError(error?.response?.data);
          checkError(error, translate("anErrorOccuredTryAgain"));
        }
      },
    });
  };

  const useInviteSuppliers = (
    supplierIds: SupplierIds,
    companyId: number,
    callback?: (supplierIds?: SupplierIds) => void
  ) => {
    return useQueries({
      queries: supplierIds.map((supplierId) => ({
        queryKey: SupplierKeys.invite(supplierId),
        queryFn: () => SupplierService.inviteToShipeezi({ supplierId, companyId }),
        enabled: false,
        refetchOnWindowFocus: false,
        retry: false,
        onSuccess: () => {
          NotificationToast.success(translate("inviteSentSuccessfully"), {
            id: "invite-supplier-success",
          });
          if (callback) callback(supplierIds);
        },
      })),
    });
  };

  const useInviteSupplier = (callback?: CallbackFuntions) => {
    return useMutation({
      mutationKey: SupplierKeys.invite(),
      mutationFn: (params: SupplierInviteParams) => SupplierService.inviteToShipeezi(params),
      onSuccess: (response) => {
        if (response && response.data) {
          NotificationToast.success(translate("inviteSentSuccessfully"), {
            id: "invite-supplier-success",
          });
          if (callback && callback.onSuccess) callback.onSuccess();
        }
      },
    });
  };

  const getSuppliersById = (
    supplierIds: SupplierIds,
    setSuppliers: React.Dispatch<React.SetStateAction<Supplier[]>>
  ) => {
    return useQuery({
      queryKey: SupplierKeys.getAll([supplierIds]),
      queryFn: () =>
        SupplierService.getAll({
          companyId: currentUser?.companyId,
          id: supplierIds,
          attributes: ["id", "name", "email", "companyId", "associatedCompanyId"],
        }),
      enabled: supplierIds.length > 0,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setSuppliers(response.data);
        }
      },
    });
  };

  return {
    getAllSuppliers,
    countSuppliers,
    getSupplier,
    useSaveSupplier,
    useInviteSupplier,
    getSuppliersById,
    useInviteSuppliers,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    responseError,
  };
};

export default useActionSupplier;

interface CallbackFuntions {
  onSuccess?: () => void;
}
